import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, GraduationCap, Mail, Loader2 } from 'lucide-react';
import { getErrorMessage } from '@/lib/errors';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(formData.email, formData.password);
      toast.success(`Welcome back, ${user.full_name}!`);
      
      // Small delay to ensure auth state is fully updated before navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Redirect based on role
      if (user.role === 'student') navigate('/student', { replace: true });
      else if (user.role === 'agent') navigate('/agent', { replace: true });
      else if (user.role === 'admin') navigate('/admin', { replace: true });
      else navigate('/', { replace: true });
    } catch (error) {
      const msg = getErrorMessage(error, 'Invalid email or password');
      // Unverified email — show a CTA toast with a Resend action so the
      // user doesn't just stare at a red toast with no path forward.
      if (/verify your email/i.test(msg)) {
        toast.error(msg, {
          duration: 10000,
          action: {
            label: 'Resend email',
            onClick: async () => {
              try {
                await authAPI.resendStudentVerification(formData.email);
                toast.success('Verification email sent. Please check your inbox.');
              } catch (e) {
                toast.error(getErrorMessage(e, 'Could not resend verification email'));
              }
            },
          },
        });
      } else if (/agent.*forgot password/i.test(msg) || /created by your agent/i.test(msg)) {
        // Agent-created student has no password yet — guide them to Forgot Password
        toast.error(msg, {
          duration: 10000,
          action: {
            label: 'Set password',
            onClick: () => {
              setForgotEmail(formData.email);
              setShowForgotPassword(true);
            },
          },
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setSendingReset(true);
    try {
      await authAPI.forgotPassword(forgotEmail);
      toast.success('If an account exists with this email, you will receive a password reset link.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 flex transition-colors">
      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white text-xl">Reset Your Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4 pt-4">
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400" />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 pl-10 h-12 dark:text-white"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
                className="flex-1 border-gray-300 text-gray-600 dark:text-gray-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sendingReset}
                className="flex-1 bg-teal-600 text-white hover:bg-teal-700 font-medium"
                data-testid="send-reset-link-btn"
              >
                {sendingReset ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        <div className="w-full max-w-md space-y-6 sm:space-y-8">
          <div className="text-center">
            <Link to="/">
              <img src={LOGO_URL} alt="H E Direct" className="h-12 sm:h-16 mx-auto mb-4 sm:mb-6" />
            </Link>
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-white mb-2">Welcome Back</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">Sign in to continue your journey</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-12 dark:text-white"
                required
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-gray-700 dark:text-gray-300">Password</Label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-teal-600 hover:text-teal-700"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-12 pr-10 dark:text-white"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-600 dark:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 text-white hover:bg-teal-700 font-medium h-12 rounded-xl"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <div className="text-center space-y-4">
            <p className="text-gray-500 dark:text-gray-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-teal-600 hover:underline font-medium">
                Sign up as Student
              </Link>
            </p>
            <p className="text-gray-500 dark:text-gray-400">
              Are you an agent?{' '}
              <Link to="/agent-register" className="text-teal-600 hover:underline font-medium">
                Register as Agent
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#f8f9fa] to-transparent z-10"></div>
        <img 
          src="https://images.pexels.com/photos/3762808/pexels-photo-3762808.jpeg"
          alt="Student"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-10 left-10 z-20 max-w-md">
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-6">
            <p className="text-gray-800 dark:text-white text-lg italic mb-4">
              "H E Direct helped me get into my dream university. Their guidance was invaluable!"
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-600/20 dark:bg-teal-900/50 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <p className="text-gray-800 dark:text-white font-medium">Success Story</p>
                <p className="text-gray-500 dark:text-gray-400 text-sm">University of Surrey</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
